/**
 * TelerikUtility manipulates Telerik ASP.NET AJAX controls used throughout iMIS pages, including data grids, tab strips, numeric text boxes, and generic input/date controls.
 */
export class TelerikUtility {

    /**
     * Finds the underlying data grid element (identified by a `data-gridid` attribute) nested within a container.
     * @param dataGridContainerSelector Selector or jQuery object for the container wrapping the data grid
     * @returns A jQuery collection containing the matching data grid element(s)
     */
    public static getDataGridFromContainer(dataGridContainerSelector: string | JQuery<any>) {
        return jQuery(dataGridContainerSelector).find("[data-gridid]");
    }

    /**
     * Rebinds a Telerik RadGrid's master table view, forcing it to reload/re-render its data.
     * @param dataGridContainerSelector Selector or element for the container wrapping the data grid to refresh
     */
    public static refreshDataGridView(dataGridContainerSelector: any) {
        let iqaGridElement = this.getDataGridFromContainer(dataGridContainerSelector);
        let gridId = iqaGridElement.data("gridid");

        let radGrid: Telerik.Web.UI.RadGrid = $find(gridId);
        let masterTable = radGrid.get_masterTableView();
        masterTable.rebind();
    }

    /**
     * Finds every Telerik RadNumericTextBox matching the given selector and removes its display formatting via {@link removeNumericTextBoxDisplayFormatting}.
     * @param controlSelector Selector matching candidate input elements to check (defaults to Telerik's standard `input.riTextBox` class)
     */
    public static removeAllNumericTextBoxDisplayFormatting(controlSelector: string = "input.riTextBox") {
        let self = this;

        jQuery(controlSelector).each(function () {
            let tControl: Telerik.Web.UI.RadWebControl = $find(this.id);
            if (tControl != null) {
                let tControlClassName = tControl.constructor["__typeName"];
                if (tControlClassName == "Telerik.Web.UI.RadNumericTextBox") {
                    self.removeNumericTextBoxDisplayFormatting(this.id);
                }
            }
        });
    }

    /**
     * Strips a Telerik numeric input control's display formatting by registering a value-changed handler that copies the raw value into the display value, then immediately raising a value-changed event to apply it.
     * @param elementId The id of the Telerik numeric input control to update
     */
    public static removeNumericTextBoxDisplayFormatting(elementId: string) {
        let tNumericControl: Telerik.Web.UI.RadInputControl = $find(elementId);

        // trigger change event after load
        tNumericControl.add_valueChanged((sender) => { sender.set_displayValue(sender.get_value()); });
        tNumericControl.raise_valueChanged(tNumericControl.get_value(), Number.MIN_VALUE);
    }

    /**
     * Finds the Telerik RadTabStrip client-side control instance within a given parent scope.
     * @param parentSelector Selector prefix scoping the search to a particular ancestor element (defaults to searching the whole document)
     * @returns The RadTabStrip client-side control instance
     */
    public static getTabStrip(parentSelector: string = ""): Telerik.Web.UI.RadTabStrip {
        let jTabStrip = this.getTabStripElement(parentSelector);
        let tTabStrip = $find<Telerik.Web.UI.RadTabStrip>(jTabStrip.attr("id") || "");

        return tTabStrip;
    }

    /**
     * Finds the id of the anchor element sibling to a RadTabStrip's parent, used to identify the tab strip's associated element on the page.
     * @param parentSelector Selector prefix scoping the search to a particular ancestor element (defaults to searching the whole document)
     * @returns The id of the matching anchor element, or an empty string if none is found
     */
    public static getTabStripUniqueId(parentSelector: string = ""): string {
        let uniqueId = "";
                
        let jTabStrip = this.getTabStripElement(parentSelector);
        let jLinks = jTabStrip.parent().siblings("a");
        if (jLinks.length > 0) {
            uniqueId = jLinks.attr("id") || "";
        }

        return uniqueId;
    }

    /**
     * Reads the current value from a Telerik web control, using `get_selectedDate` for date controls and falling back to the generic `get_value` method for other control types.
     * @param tElement The Telerik client-side control instance to read from
     * @returns The control's current value, or undefined if the control exposes neither API
     */
    public static getTelerikControlValue(tElement: Telerik.Web.UI.RadWebControl) {
        let value;

        // check specific options or use the generic type to cover all possible options
        if (this.isTelerikDateControl(tElement)) {
            value = (<Telerik.Web.UI.RadDateTimePicker>tElement).get_selectedDate();
        } else if (tElement["get_value"] != null) {
            value = tElement["get_value"]();
        }

        return value;
    }

    /**
     * Sets the value on a Telerik web control, using `set_selectedDate` for date controls and falling back to the generic `set_value` method for other control types.
     * @param tElement The Telerik client-side control instance to update
     * @param newValue The value to apply to the control
     */
    public static setTelerikControlValue(tElement: Telerik.Web.UI.RadWebControl, newValue: any) {
        // check specific options or use the generic type to cover all possible options
        if (this.isTelerikDateControl(tElement)) {
            (<Telerik.Web.UI.RadDateTimePicker>tElement).set_selectedDate(newValue);
        } else if (tElement["set_value"] != null) {
            tElement["set_value"](newValue);
        }
    }

    /**
     * Determines whether a Telerik web control is a date-based control (e.g. RadDatePicker/RadDateTimePicker) by checking for its internal `_dateInput` field.
     * @param tElement The Telerik client-side control instance to check
     * @returns True if the control is a date control, false otherwise
     */
    public static isTelerikDateControl(tElement: Telerik.Web.UI.RadWebControl) {
        return tElement["_dateInput"] != null;
    }

    /**
     * Finds the DOM element for a RadTabStrip control within a given parent scope.
     * @param parentSelector Selector prefix scoping the search to a particular ancestor element (defaults to searching the whole document)
     * @returns A jQuery collection containing the matching `.RadTabStrip` element(s)
     */
    private static getTabStripElement(parentSelector: string = "") {
        return jQuery(parentSelector + " .RadTabStrip");
    }

}